const nodeProcess = (globalThis as { process?: { env?: Record<string, string | undefined> } })
  .process;

const readEnv = (key: string, fallback: string): string => {
  // import.meta.env is replaced with a literal object at build time and is available
  // immediately at any module load order — before main.tsx sets __APP_ENV__. API clients
  // are initialized at module level, so this must be the primary source to avoid the
  // timing gap where __APP_ENV__ is still undefined and the localhost guardrail fires.
  const buildEnv = import.meta.env as Record<string, string | undefined>;
  const runtimeEnv =
    (globalThis as { __APP_ENV__?: Record<string, string | undefined> }).__APP_ENV__ ?? {};
  return buildEnv[key] ?? runtimeEnv[key] ?? nodeProcess?.env?.[key] ?? fallback;
};

const RE_APP_SUBDOMAIN = /^([^.]+)\.app\.(.+)$/;

const deriveApiOriginFromHostname = (): string => {
  const hostname = (globalThis as { location?: { hostname?: string } }).location?.hostname ?? '';
  // Convert <env>.app.<domain> -> <env>.api.<domain> (e.g., dev.app.foo.com -> dev.api.foo.com)
  const match = RE_APP_SUBDOMAIN.exec(hostname);
  if (!match) {
    return '';
  }
  return `https://${match[1]}.api.${match[2]}`;
};

const resolveApiUrl = (): string => {
  const configured = readEnv('VITE_API_URL', '/api/v1');
  const apiOrigin = deriveApiOriginFromHostname();
  const hostname = (globalThis as { location?: { hostname?: string } }).location?.hostname ?? '';
  const isLocalHost =
    hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0';

  // If a relative API path is configured for an app subdomain, make it absolute to api subdomain.
  if (configured.startsWith('/') && apiOrigin) {
    return `${apiOrigin}${configured}`;
  }

  // Guardrail: never use a localhost API when the frontend is served on a non-local host.
  if (
    (configured.includes('://localhost') || configured.includes('://127.0.0.1')) &&
    !isLocalHost
  ) {
    if (apiOrigin) {
      return `${apiOrigin}/api/v1`;
    }
    return '/api/v1';
  }

  return configured;
};

// Getters ensure each value is resolved lazily at access time, not at module-evaluation time.
// This means __APP_ENV__ (set by main.tsx) is always visible when config properties are read,
// even though config.ts is imported (and its module body evaluated) before main.tsx body runs.
export const config = {
  get apiUrl() {
    return resolveApiUrl();
  },
  get environment() {
    return readEnv('MODE', 'development');
  },
  get ssoProvider() {
    return readEnv('VITE_SSO_PROVIDER', 'entra');
  },
  // Logging — leave logLevel empty to use env-tier default (dev=DEBUG, uat=INFO, prod=WARN)
  get logLevel() {
    return readEnv('VITE_LOG_LEVEL', '');
  },
  get logTransports() {
    return readEnv('VITE_LOG_TRANSPORTS', 'console');
  },
  get datadogApiKey() {
    // VITE_DATADOG_API_KEY is scoped to logs:write only (verified 2026-10-07).
    // Rotate if additional scopes are added to this key.
    return readEnv('VITE_DATADOG_API_KEY', '');
  },
  get datadogSite() {
    return readEnv('VITE_DATADOG_SITE', 'datadoghq.com');
  },
  get newrelicLicenseKey() {
    return readEnv('VITE_NEWRELIC_LICENSE_KEY', '');
  },
  get newrelicEndpoint() {
    return readEnv('VITE_NEWRELIC_ENDPOINT', 'https://log-api.newrelic.com/log/v1');
  },
};
